import React, { useEffect, useState } from 'react';
import { Alert, Checkbox, Input, Modal, Space, Typography, message } from 'antd';
import { adminAPI } from '../services/api';
import type { ModelCandidatePackageUploadResponse } from '../types';
import { formatErrorMessage } from '../utils/errorHandler';

const { Text } = Typography;

const nextPatchVersion = (version?: string | null) => {
  const match = String(version || '').match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!match) return '1.0.0';
  return `${match[1]}.${match[2]}.${Number(match[3]) + 1}`;
};

interface ModelScriptUpgradeModalProps {
  open: boolean;
  modelId?: string;
  currentVersion?: string | null;
  onCancel: () => void;
  onCompleted?: (result: ModelCandidatePackageUploadResponse) => void | Promise<void>;
}

const ModelScriptUpgradeModal: React.FC<ModelScriptUpgradeModalProps> = ({
  open,
  modelId,
  currentVersion,
  onCancel,
  onCompleted,
}) => {
  const [version, setVersion] = useState('');
  const [rFile, setRFile] = useState<File>();
  const [cppFile, setCppFile] = useState<File>();
  const [autoQualify, setAutoQualify] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVersion(nextPatchVersion(currentVersion));
    setRFile(undefined);
    setCppFile(undefined);
    setAutoQualify(true);
  }, [currentVersion, open]);

  const submit = async () => {
    if (!modelId || !version.trim() || !rFile || !cppFile) {
      message.warning('请填写新版本号并选择 .R 与 .cpp 脚本');
      return;
    }
    setSubmitting(true);
    try {
      const result = await adminAPI.uploadModelScriptUpgrade(
        modelId,
        version.trim(),
        rFile,
        cppFile,
      );
      if (autoQualify) {
        message.loading({
          content: `模型版本 ${result.package_version} 已生成，正在运行技术资格验证`,
          key: 'model-script-upgrade',
          duration: 0,
        });
        try {
          const run = await adminAPI.qualifyModelCandidatePackage(result.id);
          message.success({
            content: run.status === 'platform_qualified'
              ? `模型版本 ${result.package_version} 已通过技术资格验证，下一步审核版本`
              : `模型版本 ${result.package_version} 已生成，验证状态：${run.status}`,
            key: 'model-script-upgrade',
          });
        } catch (error) {
          message.error({
            content: formatErrorMessage(error, `模型版本 ${result.package_version} 已生成，但技术资格验证失败`),
            key: 'model-script-upgrade',
          });
        }
      } else {
        message.success(`模型版本 ${result.package_version} 已生成，可稍后运行技术资格验证`);
      }
      await onCompleted?.(result);
      onCancel();
    } catch (error) {
      message.error(formatErrorMessage(error, '脚本快速升级失败'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={modelId ? `脚本快速升级 · ${modelId}` : '脚本快速升级'}
      okText={autoQualify ? '生成并验证' : '生成模型版本'}
      cancelText="取消"
      confirmLoading={submitting}
      okButtonProps={{ disabled: !version.trim() || !rFile || !cppFile }}
      onCancel={onCancel}
      onOk={() => void submit()}
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        message="用于已有模型的脚本迭代"
        description="系统会继承上一版 manifest、来源证据、回归向量和平台契约，仅替换 R/CPP 脚本并生成新模型版本。无需重新录入模型资料，但发布前必须重新执行技术资格验证。"
        style={{ marginBottom: 16 }}
      />
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Text strong>新版本号</Text>
          <Input
            aria-label="新版本号"
            value={version}
            placeholder="例如: 1.0.1"
            onChange={(event) => setVersion(event.target.value)}
            style={{ marginTop: 6 }}
          />
        </div>
        <div>
          <Text strong>Posologyr 脚本 (.R)</Text>
          <Input
            aria-label="Posologyr 脚本 (.R)"
            type="file"
            accept=".R,.r,text/plain"
            onChange={(event) => setRFile(event.target.files?.[0])}
            style={{ marginTop: 6 }}
          />
        </div>
        <div>
          <Text strong>mrgsolve 脚本 (.cpp)</Text>
          <Input
            aria-label="mrgsolve 脚本 (.cpp)"
            type="file"
            accept=".cpp,text/plain"
            onChange={(event) => setCppFile(event.target.files?.[0])}
            style={{ marginTop: 6 }}
          />
        </div>
        <Checkbox
          checked={autoQualify}
          onChange={(event) => setAutoQualify(event.target.checked)}
        >
          生成后立即运行技术资格验证
        </Checkbox>
      </Space>
    </Modal>
  );
};

export default ModelScriptUpgradeModal;
